// Uploaded Files List - displays list of uploaded files with status

import { Button, Label, Progress } from "@repo/ui";
import {
  AlertTriangle,
  Check,
  Clock,
  FileImage,
  FileVideo,
  Info,
  Loader2,
  RotateCcw,
  X,
} from "lucide-react";
import { formatFileSize } from "@/lib/utils/format-file-size";

export interface UploadedFile {
  id: string;
  file: File;
  url: string;
  /** Percent of the file sent, 0–100. */
  progress: number;
  /** `queued` waits for the files ahead of it to finish uploading. */
  status: "queued" | "uploading" | "done" | "error";
  error?: string;
  /** Shown on a queued file, e.g. why it paused. */
  notice?: string;
}

interface UploadedFilesListProps {
  files: UploadedFile[];
  onRemove: (fileId: string) => void;
  /** Shows a Retry button on files that failed. */
  onRetry?: (fileId: string) => void;
}

export const KEEP_PAGE_OPEN_HINT =
  "Keep this page open until your files finish uploading. Leaving the page or locking your phone can stop an upload.";

function UploadStatusLine({ uploadedFile }: { uploadedFile: UploadedFile }) {
  const { file, progress, status } = uploadedFile;
  const size = formatFileSize(file.size);

  if (status === "uploading") {
    return (
      <div className="mt-1 space-y-1">
        <Progress
          value={progress}
          className="h-1.5"
          aria-label={`Uploading ${file.name}`}
        />
        <p className="text-xs text-muted-foreground">
          Uploading… {progress}% ·{" "}
          {formatFileSize(Math.round((file.size * progress) / 100))} of {size}
        </p>
      </div>
    );
  }
  if (status === "queued") {
    return (
      <p className="text-xs text-muted-foreground">
        {size} · {uploadedFile.notice ?? "Waiting to upload…"}
      </p>
    );
  }
  if (status === "error") {
    return (
      <p className="text-xs text-destructive">
        {uploadedFile.error ?? "This file didn't upload. Please retry."}
      </p>
    );
  }
  return <p className="text-xs text-muted-foreground">{size} · Uploaded</p>;
}

export function UploadedFilesList({
  files,
  onRemove,
  onRetry,
}: UploadedFilesListProps) {
  if (files.length === 0) return null;

  const hasPendingUploads = files.some(
    (f) => f.status === "uploading" || f.status === "queued",
  );

  return (
    <div className="space-y-2">
      <Label>Uploaded Files</Label>
      {hasPendingUploads && (
        <div
          role="status"
          className="flex gap-2 rounded-lg border border-primary/20 bg-primary/5 p-2 text-xs text-muted-foreground"
        >
          <Info className="h-4 w-4 shrink-0 text-primary" aria-hidden />
          <span>{KEEP_PAGE_OPEN_HINT}</span>
        </div>
      )}
      {files.map((uploadedFile) => (
        <div
          key={uploadedFile.id}
          className="flex items-center gap-3 p-2 bg-muted rounded-lg"
        >
          {uploadedFile.file.type.startsWith("video/") ? (
            <FileVideo className="h-5 w-5 shrink-0 text-muted-foreground" />
          ) : (
            <FileImage className="h-5 w-5 shrink-0 text-muted-foreground" />
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm truncate">{uploadedFile.file.name}</p>
            <UploadStatusLine uploadedFile={uploadedFile} />
          </div>
          {uploadedFile.status === "queued" && (
            <Clock
              className="h-4 w-4 shrink-0 text-muted-foreground"
              aria-hidden
            />
          )}
          {uploadedFile.status === "uploading" && (
            <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
          )}
          {uploadedFile.status === "done" && (
            <Check className="h-4 w-4 shrink-0 text-green-500" aria-hidden />
          )}
          {uploadedFile.status === "error" &&
            (onRetry ? (
              <Button
                variant="outline"
                size="sm"
                className="h-7 shrink-0 px-2"
                onClick={() => onRetry(uploadedFile.id)}
                aria-label={`Retry ${uploadedFile.file.name}`}
              >
                <RotateCcw className="h-3.5 w-3.5 mr-1" aria-hidden />
                Retry
              </Button>
            ) : (
              <AlertTriangle
                className="h-4 w-4 shrink-0 text-destructive"
                aria-hidden
              />
            ))}
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 shrink-0"
            onClick={() => onRemove(uploadedFile.id)}
            aria-label={`Remove ${uploadedFile.file.name}`}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ))}
    </div>
  );
}
